/* eslint-disable no-unused-vars */
import React, { useMemo, useState, useEffect } from 'react';
import { PLATS, NM, COST_FIELDS, PRE_MONTHS } from '../utils/constants';
import { calcPlatform, calcIRR, getGameState, getGameCashflows } from '../utils/calc';
import { getCostState, getDealTerms } from '../utils/stateFactory';
import { fu, mlSafe } from '../utils/format';
import supabase, { orgId } from '../supabaseClient';

const STATE_COLORS = {
  'Pre-Launch': { bg: '#1a2a3a', border: '#2d6a8f', text: '#4db8e8', label: 'Pre-Launch' },
  'Pre-Recoup': { bg: '#1a2035', border: '#3d5af1', text: '#7b9eff', label: 'Pre-Recoup' },
  'Recouped':   { bg: '#0d2818', border: '#1a7a3a', text: '#00c47a', label: 'Recouped'  },
  'Unknown':    { bg: 'var(--s2)', border: 'var(--e1)', text: 'var(--t3)', label: 'Unknown' },
};

const RAG_COLORS = {
  Red:   { bg: '#2a0d0d', border: '#8b1a1a', text: '#ff4d4d', badgeBg: '#8b1a1a' },
  Amber: { bg: '#2a1a0d', border: '#8b5a1a', text: '#f5a623', badgeBg: '#8b5a1a' },
  Green: { bg: '#0d2818', border: '#1a7a3a', text: '#00c47a', badgeBg: '#1a7a3a' },
};

function MetricCard({ value, label, sub, subColor }) {
  return (
    <div style={{
      flex: 1, padding: '16px 20px', background: 'var(--s0)',
      border: '1px solid var(--e0)', borderRadius: 'var(--r2)', minWidth: 0,
    }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--t0)', lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 10, color: 'var(--t3)', marginTop: 4, letterSpacing: '.04em' }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color: subColor || 'var(--green)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function ExecSummary({
  games, PS, CS, DT, currentMonthIdx, startDate, functionalCurrency, convert, convertTotal, ragStatus,
}) {
  const fc  = functionalCurrency || 'USD';
  const now = currentMonthIdx ?? NM - 1;
  const [narrative, setNarrative]   = useState('');
  const [genLoading, setGenLoading] = useState(false);

  // Load saved narrative from Supabase
  useEffect(() => {
    supabase.from('app_settings').select('value')
      .eq('org_id', orgId).eq('key', 'exec_narrative').maybeSingle()
      .then(({ data }) => { if (data?.value) setNarrative(data.value); });
  }, []);

  const metrics = useMemo(() => {
    const activeGames = games.filter(g => PLATS.some(p => PS[g]?.[p.key]?.enabled));
    let totalInvested = 0, totalRevenue = 0;
    const irrList = [];

    activeGames.forEach(g => {
      const costs  = getCostState(CS, g);
      const dt     = getDealTerms(DT, g);
      let invested = 0;
      ['recoup','nonRecoup'].forEach(sec => {
        COST_FIELDS.forEach(({ key }) => {
          (costs[sec][key] || []).forEach(v => { invested += parseFloat(v) || 0; });
        });
      });
      invested += parseFloat(dt.devAdvanceAmt) || 0;
      totalInvested += invested;

      PLATS.forEach(p => {
        if (!PS[g]?.[p.key]?.enabled) return;
        calcPlatform(p.key, PS[g][p.key]).netRaw.forEach((v, i) => {
          if (v !== null && i <= now) totalRevenue += convert ? convert(v, fc, i) : v;
        });
      });

      const cf = getGameCashflows(PS, CS, DT, g, PLATS);
      const first = cf.findIndex(v => v !== 0);
      const irr = first >= 0 ? calcIRR(cf.slice(first)) : null;
      if (irr !== null) irrList.push(Math.pow(1 + irr, 12) - 1);
    });

    const portfolioROI    = totalInvested > 0 ? totalRevenue / totalInvested : 0;
    const portfolioIRR    = irrList.length ? irrList.reduce((a, v) => a + v, 0) / irrList.length : null;
    const gamesAtRisk     = games.filter(g => ragStatus[g] === 'Red').length;
    const gamesAmber      = games.filter(g => ragStatus[g] === 'Amber').length;
    const gamesGreen      = games.filter(g => ragStatus[g] === 'Green').length;

    // State counts
    const stateCounts = { 'Pre-Launch': 0, 'Pre-Recoup': 0, 'Recouped': 0 };
    activeGames.forEach(g => {
      const s = getGameState(PS, CS, DT, g, PLATS, currentMonthIdx);
      if (stateCounts[s] !== undefined) stateCounts[s]++;
    });

    // Avg recoup months
    const recoupedGames = activeGames.filter(g => getGameState(PS, CS, DT, g, PLATS, currentMonthIdx) === 'Recouped');
    // Estimate recoup month per game
    const recoupMonths = recoupedGames.map(g => {
      const netArr = Array(NM).fill(0);
      const recArr = Array(NM).fill(0);
      PLATS.forEach(p => {
        if (!PS[g]?.[p.key]?.enabled) return;
        calcPlatform(p.key, PS[g][p.key]).netRaw.forEach((v, i) => { if (v !== null) netArr[i] += v; });
      });
      const costs = getCostState(CS, g);
      COST_FIELDS.forEach(({ key }) => {
        costs.recoup[key].forEach((v, ai) => { const i = ai - PRE_MONTHS; if (i >= 0 && i < NM) recArr[i] += parseFloat(v) || 0; });
      });
      let cum = 0;
      for (let i = 0; i < NM; i++) { cum += (netArr[i] || 0) - (recArr[i] || 0); if (cum > 0) return i; }
      return null;
    }).filter(m => m !== null);
    const avgRecoup = recoupMonths.length ? Math.round(recoupMonths.reduce((a, v) => a + v, 0) / recoupMonths.length) : null;

    // FY progress
    const fyStart = 0;
    const fyEnd   = Math.min(11, NM - 1);
    let fyRevForecast = 0, fyRevToDate = 0;
    activeGames.forEach(g => {
      PLATS.forEach(p => {
        if (!PS[g]?.[p.key]?.enabled) return;
        calcPlatform(p.key, PS[g][p.key]).netRaw.forEach((v, i) => {
          if (v !== null && i >= fyStart && i <= fyEnd) {
            const vFC = convert ? convert(v, fc, i) : v;
            fyRevForecast += vFC;
            if (i <= now) fyRevToDate += vFC;
          }
        });
      });
    });

    let fyOhForecast = 0, fyOhToDate = 0;
    activeGames.forEach(g => {
      const costs = getCostState(CS, g);
      ['recoup','nonRecoup'].forEach(sec => {
        COST_FIELDS.forEach(({ key }) => {
          (costs[sec][key] || []).forEach((v, ai) => {
            const i = ai - PRE_MONTHS;
            if (i >= fyStart && i <= fyEnd) {
              fyOhForecast += parseFloat(v) || 0;
              if (i <= now) fyOhToDate += parseFloat(v) || 0;
            }
          });
        });
      });
    });

    return {
      activeGames: activeGames.length,
      totalInvested: convert ? convert(totalInvested, fc, now) : totalInvested,
      totalRevenue,
      portfolioROI,
      portfolioIRR,
      gamesAtRisk,
      gamesAmber,
      gamesGreen,
      stateCounts,
      avgRecoup,
      fyRevForecast, fyRevToDate,
      fyOhForecast,  fyOhToDate,
      fyNetForecast: fyRevForecast - fyOhForecast,
      fyNetToDate:   fyRevToDate   - fyOhToDate,
    };
  }, [games, PS, CS, DT, currentMonthIdx, now, fc, convert, ragStatus]);

  const atRiskGames   = games.filter(g => ragStatus[g] === 'Red');
  const amberGames    = games.filter(g => ragStatus[g] === 'Amber');
  const monthLabel    = startDate ? mlSafe(now, startDate) : `Month ${now + 1}`;
  const fyLabel       = startDate ? `FY${startDate.year}/${String(startDate.year + 1).slice(2)}` : 'FY Forecast';

  async function generateNarrative() {
    setGenLoading(true);
    try {
      const prompt = `You are a games publishing CFO writing a brief board-level executive summary. 
Based on these metrics, write 2-3 concise sentences for a monthly board report. Be specific with numbers. Professional tone.

Month: ${monthLabel}
Active Games: ${metrics.activeGames}
Total Invested (${fc}): ${fu(metrics.totalInvested)}
Total Revenue to Date (${fc}): ${fu(metrics.totalRevenue)}
Portfolio ROI: ${metrics.portfolioROI.toFixed(2)}x
Portfolio IRR: ${metrics.portfolioIRR !== null ? Math.round(metrics.portfolioIRR * 100) + '%' : 'N/A'}
Games at Risk (Red RAG): ${metrics.gamesAtRisk}
Games Amber: ${metrics.gamesAmber}
Games Green: ${metrics.gamesGreen}
Pre-Launch: ${metrics.stateCounts['Pre-Launch']}, Pre-Recoup: ${metrics.stateCounts['Pre-Recoup']}, Recouped: ${metrics.stateCounts['Recouped']}
FY Revenue: ${fu(metrics.fyRevToDate)} actual vs ${fu(metrics.fyRevForecast)} forecast (${metrics.fyRevForecast > 0 ? Math.round(metrics.fyRevToDate / metrics.fyRevForecast * 100) : 0}% of plan)
FY Overhead: ${fu(metrics.fyOhToDate)} actual vs ${fu(metrics.fyOhForecast)} forecast

Write the headline narrative only. No headers, no bullet points.`;

      const res = await fetch('/api/generateNarrative', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt }),
      });
      const data = await res.json();
      if (data.error) {
        setNarrative('⚠ ' + data.error);
      } else {
        setNarrative(data.text || '');
      }
    } catch (e) {
      setNarrative('Error generating narrative. Please try again.');
    }
    setGenLoading(false);
  }

  const pct = (a, b) => b > 0 ? Math.round(a / b * 100) + '% of plan' : '—';

  return (
    <div>
      {/* Generate button above narrative */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button onClick={generateNarrative} disabled={genLoading}
          style={{ background: 'var(--blue-dim)', border: '1px solid var(--blue)',
            color: 'var(--blue)', borderRadius: 'var(--r)', padding: '6px 14px',
            fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase',
            cursor: genLoading ? 'wait' : 'pointer', fontFamily: 'var(--sans)' }}>
          {genLoading ? 'Generating…' : '✦ Generate Narrative'}
        </button>
      </div>
      {/* Narrative box */}
      <div style={{
        background: 'var(--s0)', border: '1px solid var(--e0)', borderRadius: 'var(--r2)',
        padding: '14px 18px', marginBottom: 20, minHeight: 48,
      }}>
        {narrative ? (
          <p style={{ fontSize: 13, color: 'var(--t1)', lineHeight: 1.6, margin: 0 }}>
            <strong style={{ color: 'var(--t0)' }}>Headline: </strong>{narrative}
          </p>
        ) : (
          <p style={{ fontSize: 12, color: 'var(--t4)', margin: 0, fontStyle: 'italic' }}>
            Click "Generate Narrative" to produce a board-ready summary from current data.
          </p>
        )}
      </div>

      {/* Metrics row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 20 }}>
        <MetricCard value={metrics.activeGames} label="Active Games"
          sub={metrics.activeGames > 0 ? `+${metrics.activeGames}` : null} subColor="var(--green)" />
        <MetricCard value={fu(metrics.totalInvested)} label="Total Invested" />
        <MetricCard value={fu(metrics.totalRevenue)} label="Total Revenue" />
        <MetricCard
          value={isNaN(metrics.portfolioROI) || !isFinite(metrics.portfolioROI) ? '—' : metrics.portfolioROI.toFixed(2) + 'x'}
          label="Portfolio ROI"
          sub={!isNaN(metrics.portfolioROI) && isFinite(metrics.portfolioROI) ? (metrics.portfolioROI >= 1 ? 'above par' : 'below par') : 'no data yet'}
          subColor={metrics.portfolioROI >= 1 ? 'var(--green)' : '#f5a623'} />
        <MetricCard
          value={metrics.portfolioIRR !== null ? Math.round(metrics.portfolioIRR * 100) + '%' : '—'}
          label="Portfolio IRR" sub="annualised" subColor="var(--green)" />
        <MetricCard
          value={metrics.gamesAtRisk}
          label="Games At Risk"
          sub={metrics.gamesAtRisk === 0 ? 'none' : 'requires attention'}
          subColor={metrics.gamesAtRisk > 0 ? '#ff4d4d' : 'var(--green)'} />
        <MetricCard
          value={metrics.avgRecoup !== null ? metrics.avgRecoup + ' mo' : '—'}
          label="Avg Recoup" />
      </div>

      {/* Status Breakdown */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--t2)', marginBottom: 10, letterSpacing: '.04em' }}>
          Status Breakdown
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {[
            { label: 'GREEN', count: metrics.gamesGreen, ...RAG_COLORS.Green },
            { label: 'AMBER', count: metrics.gamesAmber, ...RAG_COLORS.Amber },
            { label: 'RED',   count: metrics.gamesAtRisk, ...RAG_COLORS.Red   },
          ].map(s => (
            <div key={s.label} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 16px', borderRadius: 'var(--r2)',
              background: s.bg, border: `1px solid ${s.border}`,
            }}>
              <span style={{
                background: s.badgeBg, color: '#fff',
                fontSize: 9, fontWeight: 700, letterSpacing: '.1em',
                padding: '3px 8px', borderRadius: 3,
              }}>{s.label}</span>
              <span style={{ fontSize: 12, color: s.text }}>{s.count} game{s.count !== 1 ? 's' : ''}</span>
            </div>
          ))}
          {Object.entries(metrics.stateCounts).filter(([,v]) => v > 0).map(([state, count]) => {
            const sc = STATE_COLORS[state] || STATE_COLORS.Unknown;
            return (
              <div key={state} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 16px', borderRadius: 'var(--r2)',
                background: sc.bg, border: `1px solid ${sc.border}`,
              }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: sc.text, letterSpacing: '.06em' }}>{state}</span>
                <span style={{ fontSize: 12, color: sc.text }}>{count} game{count !== 1 ? 's' : ''}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* At Risk callout rows */}
      {[...atRiskGames.map(g => ({ g, rag: 'Red' })), ...amberGames.map(g => ({ g, rag: 'Amber' }))].map(({ g, rag }) => {
        const rc = RAG_COLORS[rag];
        return (
          <div key={g} style={{
            display: 'flex', alignItems: 'center', gap: 14,
            padding: '10px 16px', marginBottom: 8,
            background: rc.bg, border: `1px solid ${rc.border}`, borderRadius: 'var(--r2)',
          }}>
            <span style={{ background: rc.badgeBg, color: '#fff', fontSize: 9, fontWeight: 700, letterSpacing: '.1em', padding: '3px 8px', borderRadius: 3 }}>{rag.toUpperCase()}</span>
            <span style={{ fontSize: 13, color: rc.text, fontWeight: 500 }}>{g}</span>
          </div>
        );
      })}

      {/* FY Progress */}
      <div style={{ background: 'var(--s0)', border: '1px solid var(--e0)', borderRadius: 'var(--r2)', padding: '14px 20px', marginTop: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--blue)', marginBottom: 12, letterSpacing: '.04em' }}>
          {fyLabel} Progress
        </div>
        <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
          {[
            { label: 'Revenue', toDate: metrics.fyRevToDate, forecast: metrics.fyRevForecast },
            { label: 'Overhead', toDate: metrics.fyOhToDate, forecast: metrics.fyOhForecast },
            { label: 'Net Income', toDate: metrics.fyNetToDate, forecast: metrics.fyNetForecast },
          ].map(item => {
            const p = item.forecast > 0 ? Math.round(item.toDate / item.forecast * 100) : 0;
            const color = p >= 90 ? 'var(--green)' : p >= 60 ? 'var(--amber)' : '#ff4d4d';
            return (
              <div key={item.label}>
                <div style={{ fontSize: 10, color: 'var(--t3)', marginBottom: 4 }}>{item.label}:</div>
                <div style={{ fontSize: 13, color: 'var(--t1)', fontWeight: 500 }}>
                  {fu(item.toDate)} / {fu(item.forecast)}
                </div>
                <div style={{ fontSize: 10, color, marginTop: 2 }}>{pct(item.toDate, item.forecast)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
